'use client';

import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { CustomError } from '@core/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { AuthMutation, AuthQueryOptions } from '@/api';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { CUSTOM_SCHEME } from '@/modules/wello-app';
import { formatWelloPoint } from '@/utils/formatWelloPoint';
import type { BenefitMenuCpc } from '../_utils/resolveBenefitMenuGroups';
import { useBenefitGate } from './useBenefitGate';
const CPC_REJECT_RETURN_CODES = [30400, 30404];
const DEFAULT_ERROR_MESSAGE = '적립에 실패했어요. 다시 시도해 주세요.';
export const resolveCpcClickErrorMessage = (error: unknown) => error instanceof CustomError && CPC_REJECT_RETURN_CODES.includes(error.return_code) && error.return_message ? error.return_message : DEFAULT_ERROR_MESSAGE;
interface CpcClickTarget {
  benefitMenuId: string;
  url: string;
  cpc: BenefitMenuCpc;
}
export const useCpcClick = () => {
  const $ = _c(12);
  const queryClient = useQueryClient();
  const router = useCustomRouter();
  const {
    toast
  } = useToast();
  const {
    triggerGate,
    gateModals
  } = useBenefitGate();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const claimedMenuIdsRef = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = AuthMutation["post:/wello/v2/auth/benefit-menus/{benefitMenuId}/cpc-click"]();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient || $[3] !== toast) {
    t2 = {
      ...t1,
      onSuccess: response => {
        const addedPoints = response.context?.addPoints;
        toast({
          message: addedPoints ? `${formatWelloPoint(addedPoints)}이 적립됐어요` : "\uC801\uB9BD\uB410\uC5B4\uC694"
        });
        return Promise.all([queryClient.invalidateQueries({
          queryKey: AuthQueryOptions["get:/wello/v2/auth/benefit-menus"]().queryKey
        }), queryClient.invalidateQueries({
          queryKey: AuthQueryOptions["get:/wello/v2/auth/point"]().queryKey
        })]);
      },
      onError: (error, variables) => {
        claimedMenuIdsRef.current.delete(variables.path.benefitMenuId);
        toast({
          message: resolveCpcClickErrorMessage(error),
          type: "fail"
        });
        return queryClient.invalidateQueries({
          queryKey: AuthQueryOptions["get:/wello/v2/auth/benefit-menus"]().queryKey
        });
      }
    };
    $[2] = queryClient;
    $[3] = toast;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    mutate: claim
  } = useMutation(t2);
  let t3;
  if ($[5] !== claim || $[6] !== router || $[7] !== triggerGate) {
    t3 = t4 => {
      const {
        benefitMenuId,
        url,
        cpc
      } = t4;
      triggerGate(["app", "login", "consent"], () => {
        if (url.startsWith("/")) {
          router.push(url);
        } else {
          window.location.href = `${CUSTOM_SCHEME.EXTERNAL_PREFIX}${url}`;
        }
        if (cpc.isParticipated || claimedMenuIdsRef.current.has(benefitMenuId)) {
          return;
        }
        claimedMenuIdsRef.current.add(benefitMenuId);
        claim({
          path: {
            benefitMenuId
          }
        });
      });
    };
    $[5] = claim;
    $[6] = router;
    $[7] = triggerGate;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const clickCpc = t3;
  let t4;
  if ($[9] !== clickCpc || $[10] !== gateModals) {
    t4 = {
      clickCpc,
      gateModals
    };
    $[9] = clickCpc;
    $[10] = gateModals;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};